// src/components/HubFeed.jsx
"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
// The shared Firestore instance remains stable across tab mounts.
import { db } from "@/firebase/config";
import {
  collection, query, orderBy, limit, onSnapshot, getDocs, withLabel
} from "@/lib/firestore-instrumented";
import { fetchExploreHubs } from "@/lib/hubData";
import HubCard from "@/components/HubCard";
import CardSkeleton from "@/components/CardSkeleton";
import CardList from "@/components/CardList";

function HeaderAction() {
  return (
    <div className="mb-2 flex items-center justify-end">
      <Link href="/new-hub" className="btn">Create a Space</Link>
    </div>
  );
}

export default function HubFeed({
  pageSize = 25,
  live = true,
  allowedHubSlugs = null,
}) {
  const [rows, setRows] = useState(null); // null=loading, []=empty
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    if (Array.isArray(allowedHubSlugs)) {
      let cancelled = false;
      setRows(null);
      setError("");
      fetchExploreHubs(pageSize, allowedHubSlugs)
        .then((hubs) => {
          if (cancelled) return;
          setRows(hubs);
        })
        .catch((e) => {
          if (cancelled) return;
          console.error("HubFeed portal getDocs error:", e);
          setRows([]);
          setError("Could not load spaces.");
        });
      return () => {
        cancelled = true;
      };
    }
    const base = collection(db, "hubs");
    const qRef = withLabel(
      query(base, orderBy("slug", "asc"), limit(pageSize)),
      live ? `HubFeed:live:hubs:slug(limit=${pageSize})` : `HubFeed:once:hubs:slug(limit=${pageSize})`
    );

    const applyVisibility = (docs = []) =>
      docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((hub) => hub?.publicReadable !== false && hub?.type !== "private");

    if (!live) {
      // One-time fetch (no ongoing reads)
      getDocs(qRef)
        .then((snap) => {
          const next = applyVisibility(snap.docs);
          setRows(next);
          setError("");
        })
        .catch((e) => {
          console.error("HubFeed getDocs error:", e);
          setRows([]);
          setError("Could not load spaces.");
        });
      return; // no unsubscribe needed
    }

    // Keep Explore current only while its tab owns this component.
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const next = applyVisibility(snap.docs);
        setRows(next);
        setError("");
      },
      (e) => {
        console.error("HubFeed onSnapshot error:", e);
        setRows([]);
        setError("Could not load spaces.");
      }
    );
    return () => unsub();
  }, [pageSize, live, allowedHubSlugs]); // db is stable

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  if (rows === null) {
    return (
      <>
        {!allowedHubSlugs ? <HeaderAction /> : null}
        <HubSkeletonList />
      </>
    );
  }
  if (error) {
    return (
      <>
        {!allowedHubSlugs ? <HeaderAction /> : null}
        <div className="edge-mobile">
          <div className="card square-mobile text-sm text-red-600">{error}</div>
        </div>
      </>
    );
  }
  if (rows.length === 0) {
    return (
      <>
        {!allowedHubSlugs ? <HeaderAction /> : null}
        <div className="edge-mobile">
          <div className="card square-mobile text-sm text-[var(--muted)]">No spaces yet.</div>
        </div>
      </>
    );
  }

  return (
    <>
      {!allowedHubSlugs ? <HeaderAction /> : null}
      <CardList>
        {rows.map((h) => (
          <HubCard
            key={h.id}
            hub={h}
            isOpen={expanded.has(h.id)}
            onToggle={() => toggle(h.id)}
          />
        ))}
      </CardList>
    </>
  );
}

function HubSkeletonList() {
  return (
    <CardList>
      <CardSkeleton />
      <CardSkeleton />
      <CardSkeleton />
      <CardSkeleton />
    </CardList>
  );
}
